import React, { useState, useMemo, useEffect, useRef } from 'react'
import type { CardTextWrite } from '../types/cardWrite'
import { Box, Button, Chip, CircularProgress, Alert, Typography, Paper, Tabs, Tab, IconButton, Tooltip, LinearProgress, Link, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import EditOffIcon from '@mui/icons-material/EditOff'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import FilterListIcon from '@mui/icons-material/FilterList'
import RefreshIcon from '@mui/icons-material/Refresh'
import HistoryIcon from '@mui/icons-material/History'
import CachedIcon from '@mui/icons-material/Cached'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import InsightsIcon from '@mui/icons-material/Insights'
import axios from 'axios'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import { usePageJob } from '../contexts/pageJob'
import useDashboards from '../hooks/useDashboards'
import useBrandPalette from '../hooks/useBrandPalette'
import useSnackbar from '../hooks/useSnackbar'
import useDashboardInsightsChat from '../hooks/useDashboardInsightsChat'
import useAiAccess from '../hooks/useAiAccess'
import useInsightsPersonas from '../hooks/useInsightsPersonas'
import useStoryBoardEditLink from '../hooks/useStoryBoardEditLink'
import DashboardLayoutComponent from '../components/dashboard/DashboardLayoutComponent'
import { FilterSignpostProvider, FilterSignpostValue } from '../components/dashboard/FilterSignpost'
import { DashboardJobsProvider, DashboardJobsValue } from '../components/dashboard/DashboardJobs'
import DashboardHistoryModal from '../components/dashboard/DashboardHistoryModal'
import ReviewChangesPanel from '../components/dashboard/ReviewChangesPanel'
import ShareUpdateDialog from '../components/dashboard/ShareUpdateDialog'
import SignOffShareButtons from '../components/dashboard/SignOffShareButtons'
import DashboardSettingsModal from '../components/dashboard/DashboardSettingsModal'
import { applyDashboardSettingsSave } from '../components/dashboard/dashboardSettingsSave'
import FilterEditorModal from '../components/spec-editor/FilterEditorModal'
import ChatDrawer from '../components/chat/ChatDrawer'
import PageAgentButton from '../agent/components/PageAgentButton'
import PageAgentDrawer from '../agent/components/PageAgentDrawer'
import { canPromoteDashboard } from '../utils/roleChecks'
import { canOverwriteDashboard, noEditPermissionReason, noSignOffDashboardReason } from '../utils/dashboardOwnership'
import DisabledReason from '../components/system/DisabledReason'
import useWhoToAsk from '../hooks/useWhoToAsk'
import { DashboardCell, DashboardConfig, DashboardLayout, MultiTableAnalysisWidgetData, WebappDashboardConfig } from '../types/gotypes'
import { resolvePageColorMap, themeColorMapsFromResolved, themeLookupFromResolved } from '../utils/brandTheme'
import { resolveDashboardLoadState } from '../utils/dashboardLoadState'
import { applyQuickFilters, compoundFilters, insightsSourceLines, jobFilterMap, resolveWidgetFilter } from '../utils/dashboardFilters'
import { pinRollingColumnsForSignOff } from '../utils/dashboardSignOffPin'
import { captureTableSnapshotsForSignOff } from '../utils/dashboardSignOffSnapshot'
import { extractColumnLabels, extractRowLabels } from '../components/platinum-graphs/utils/convertPlatinumData'
import { widgetDomId } from '../utils/dashboardWidgetWalk'
import { ReviewChangeItem } from '../utils/reviewChanges'
import useReviewChanges from '../hooks/useReviewChanges'
import { API_BASE_URL } from '../constants/system'
import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import { buildVoiceTools } from '../voice/buildVoiceTools'
import { useDisplayNames } from '../utils/displayNames'
import DownloadPdfButton from '../components/dashboard/DownloadPdfButton'
import VideoDashboardView from '../components/dashboard/VideoDashboardView'
import { dashboardTypeMeta, isWebappBundle } from '../utils/dashboardType'
import { DashboardGrantContext } from '../contexts/DashboardGrantContext'
import usePublishedPresentation from '../hooks/usePublishedPresentation'
import PresentationAskDialog from '../components/agent/PresentationAskDialog'
import StartChatFromPageButton from '../components/agent/StartChatFromPageButton'

function WebappDashboardView({ customer, dashboardId, name, config, onBack, onEditStory, editOpening, editError }: {
  customer: string
  dashboardId: string
  /** The dashboard's name, for the new chat's chip. */
  name?: string
  config: WebappDashboardConfig
  onBack: () => void
  /** Open the story board editor on the session page (#1416). Absent for people who may not edit. */
  onEditStory?: () => void
  editOpening?: boolean
  editError?: string | null
}) {
  const authHeader = axios.defaults.headers.common['Authorization'] as string | undefined
  const token = authHeader?.replace('Bearer ', '')
  const frameRef = useRef<HTMLIFrameElement>(null)
  // A presentation (presentations): ?at= opens it at a slide, and Ask starts
  // a new chat from here. This route needs a signed-in user of the customer.
  const presentation = usePublishedPresentation({ frameRef, customer, dashboardId, job: config.job, signedIn: true, inCustomer: true, name })
  const src = `${API_BASE_URL}/dashboards/${customer}/${dashboardId}/webapp/${config.entry_point}${token ? `?token=${token}` : ''}${presentation.initialHash}`
  const ask = presentation.ask
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'white',
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} size="small">DataViz</Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {editError && <Typography sx={{ fontSize: 13, color: 'error.main' }}>{editError}</Typography>}
          <DownloadPdfButton frameRef={frameRef} />
          <StartChatFromPageButton onStart={presentation.startChat.handleStart} reason={presentation.startChat.reason} pending={presentation.startChat.pending} />
          {onEditStory && (
            <Tooltip title="Edit the words and sections, then Publish to update this dashboard">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="info"
                  startIcon={editOpening ? <CircularProgress size={14} color="info" /> : <EditIcon fontSize="small" />}
                  disabled={editOpening}
                  onClick={onEditStory}
                  data-testid="dashboard-edit-story"
                  sx={{ textTransform: 'none' }}
                >
                  Edit story board
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box sx={{ flex: 1 }}>
        <iframe
          ref={frameRef}
          src={src}
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-modals"
          style={{ width: '100%', height: '100%', border: 'none' }}
          title="Web app"
        />
      </Box>
      <PresentationAskDialog
        open={!!ask.request}
        breadcrumb={ask.breadcrumb}
        figureText={ask.figureText}
        question={ask.question}
        onStartNewChat={ask.handleStartNewChat}
        startNewChatReason={ask.startReason}
        onClose={ask.handleClose}
      />
    </Box>
  )
}

/** Collect multi-table analysis widget data from a layout (recursing into nested layouts) */
function scanLayoutForWidgets(layout: DashboardLayout): MultiTableAnalysisWidgetData[] {
  const widgets: MultiTableAnalysisWidgetData[] = []
  for (const row of layout.rows) {
    for (const cell of row.cells) {
      if (cell.widget_type === 'multi_table_analysis' && cell.multi_table_analysis_data?.multi_table_id) {
        widgets.push(cell.multi_table_analysis_data)
      }
      if (cell.widget_type === 'layout' && cell.layout_data) {
        widgets.push(...scanLayoutForWidgets(cell.layout_data))
      }
      // Report pillars (#1080) hold their cards as entries, not in a layout.
      if (cell.widget_type === 'report_pillar' && cell.report_pillar_data) {
        widgets.push(...scanLayoutForWidgets({
          rows: cell.report_pillar_data.entries.map(entry => ({ cells: [entry.cell] })),
        }))
      }
    }
  }
  return widgets
}

/** Collect all multi-table analysis widget data from all pages */
function getMultiTableAnalysisWidgets(config: DashboardConfig): MultiTableAnalysisWidgetData[] {
  if (config.pages && config.pages.length > 0) {
    return config.pages.flatMap(page => scanLayoutForWidgets(page.layout))
  }
  return scanLayoutForWidgets(config.layout)
}

/** Collect all unique jobs from table_graph widgets across all pages */
function getTableGraphJobs(config: DashboardConfig): Set<string> {
  const jobs = new Set<string>()
  const scanLayout = (layout: DashboardLayout) => {
    for (const row of layout.rows) {
      for (const cell of row.cells) {
        if (cell.widget_type === 'table_graph' && cell.table_graph_data?.job) {
          jobs.add(cell.table_graph_data.job)
        }
      }
    }
  }
  if (config.pages && config.pages.length > 0) {
    for (const page of config.pages) {
      scanLayout(page.layout)
    }
  } else {
    scanLayout(config.layout)
  }
  return jobs
}

const DashboardView: React.FC = () => {
  const router = useRouter()
  const account = useAccount()

  const customer = router.params.customer || account.customer?.name || ''
  const dashboardId = router.params.dashboardId || ''
  const job = account.job?.name || '' // Get job from account session for multi-table widgets

  const {
    dashboard,
    dashboardIsLoading,
    dashboardIsError,
    dashboardError,
    updateDashboard,
    shareDashboard,
    shareIsLoading,
    writeDashboardCard,
    forkDashboard,
} = useDashboards({ customer, dashboardId })
  const whoToAsk = useWhoToAsk(customer)
  const [duplicating, setDuplicating] = useState(false)
  // "Edit story board" (#1416): asked only for a Manager or the person who
  // published this web app dashboard; shown only to someone it will open for.
  const viewerEmail = account.user?.session_info?.email || ''
  const viewerIsManager = !!account.user?.role_permissions?.isManager
  const storyEditLink = useStoryBoardEditLink(
    isWebappBundle(dashboard?.type) ? dashboard?.config?.webapp_config : null,
    {
      isManager: viewerIsManager,
      userEmail: viewerEmail,
      mayTry: viewerIsManager || (!!dashboard?.user && dashboard.user.toLowerCase() === viewerEmail.toLowerCase()),
    },
  )

  const brandPalette = useBrandPalette(customer, job)

  const snackbar = useSnackbar()

  const [activePageIndex, setActivePageIndex] = useState(0)
  const [filterOpen, setFilterOpen] = useState(false)
  // Quick filters (#1229 C7, Matt 3 Sep): viewer-local, never saved, held
  // PER JOB, applied across the whole dashboard, and APPENDED to each page's
  // stored filter — never replacing it. Several jobs can be active at once
  // ("males in Prius and males in Brand Equity"). `quickFilterJob` is the
  // job a chip click preselects in the dialog.
  const [quickFilters, setQuickFilters] = useState<Record<string, string>>({})
  const [quickFilterJob, setQuickFilterJob] = useState('')
  const [historyOpen, setHistoryOpen] = useState(false)
  // The page helper (Douglas plan T15). It shares the right edge with the
  // Insights pane, so opening one closes the other.
  const [helperOpen, setHelperOpen] = useState(false)
  const [signOffConfirmOpen, setSignOffConfirmOpen] = useState(false)
  const [reviewPanelOpen, setReviewPanelOpen] = useState(false)
  const [shareUpdateOpen, setShareUpdateOpen] = useState(false)
  const [aboutSettingsOpen, setAboutSettingsOpen] = useState(false)

  // Insights pane (#974) — this is the authenticated read-only viewer, a
  // separate component from the unauthenticated share-link viewer
  // (SharedDashboardView.tsx), so it's safe to mount here too.
  // Bound to the page the reader is on when they open the pane, plus that
  // view's quick filter (#974 Stage 1, #1064). `pages`/`effectiveFilter` are
  // re-derived further down for rendering; these reads are the same values,
  // needed here because the hook owns the binding.
  // `config` itself is only bound after the early returns below, so read it
  // off the dashboard directly — hooks must run unconditionally.
  const insightsPages = dashboard?.config?.pages || []
  const insightsPage = insightsPages[activePageIndex] || insightsPages[0]
  const insightsPrimaryJob = dashboard?.jobs?.[0] || dashboard?.job || ''
  // #1240 B1: per-job framing — every job this view draws from, its display
  // name, and the page/quick filters on it for the page the pane binds to.
  // Same fields the server builds the model's "Data sources" block from;
  // insightsSourceLines renders the reader's copy.
  const insightsJobs = dashboard?.jobs?.length ? dashboard.jobs : (dashboard?.job ? [dashboard.job] : [])
  const insightsSessionJobs = account.customer?.sessionJobs || []
  const insightsStoredFilters = dashboard?.config ? jobFilterMap(dashboard.config, insightsPage, insightsJobs, insightsPrimaryJob) : {}
  const insightsSources = insightsJobs.map((j) => ({
    job: j,
    display_name: insightsSessionJobs.find((sj) => sj.name === j)?.displayName || j,
    page_filter: insightsStoredFilters[j] || '',
    quick_filter: quickFilters[j] || '',
  }))
  const insights = useDashboardInsightsChat({
    customer,
    dashboardId,
    pageId: insightsPage?.id || '',
    pageName: insightsPage?.name || '',
    // #1183: insights read the same resolved (dashboard AND page) filter
    // the page's widgets run under, with the primary job's quick filter
    // appended (#1229 C7) — insights are answered in the primary job.
    filter: compoundFilters(
      resolveWidgetFilter(dashboard?.config, insightsPage, insightsPrimaryJob, insightsPrimaryJob),
      quickFilters[insightsPrimaryJob],
    ),
    sources: insightsSources,
  })
  // AI access (#1537): the pane has no cache, so opening it always makes new
  // insight. Below level 2 the button is greyed; the server refuses too.
  const aiAccess = useAiAccess(customer)
  const canInsights = aiAccess.can('dashboard_insights')
  // Persona list for the Insights pane (#974 ask 1) — same source as the
  // Tables pane: defaults + custom personas from the job-level merged config.
  const insightsPersonas = useInsightsPersonas(customer)

  // Publish this dashboard's own job to the header's locked Job pill. The
  // session job is not it — a dashboard keeps the job it was created with
  // (#961) whatever the picker says. A legacy dashboard with no job publishes
  // '' and the header shows no pill.
  // A multi-job dashboard publishes its whole set (#1229 C1: pill reads
  // "Multi").
  const { setPageJobs } = usePageJob()
  const headerJobs = dashboard?.jobs?.length ? dashboard.jobs : (dashboard?.job ? [dashboard.job] : [])
  const headerJobsKey = headerJobs.join('\u0000')
  useEffect(() => {
    setPageJobs(headerJobs)
    return () => setPageJobs([])
  }, [headerJobsKey])

  // Per-PAGE colours (#1182): the active page's theme reference resolved via
  // the server-embedded resolved_themes map, merged over the job palette.
  const colorMap = useMemo(() => {
    const base = brandPalette.palette || dashboard?.config?.brand_palette
    const page = (dashboard?.config?.pages || [])[activePageIndex] || (dashboard?.config?.pages || [])[0]
    return resolvePageColorMap(page, themeLookupFromResolved(dashboard?.resolved_themes), base)
  }, [brandPalette.palette, dashboard?.config?.brand_palette, dashboard?.config?.pages, dashboard?.resolved_themes, activePageIndex])

  // Chart-level themes (#1193), from the server-embedded resolved map.
  const themeColorMaps = useMemo(
    () => themeColorMapsFromResolved(dashboard?.resolved_themes),
    [dashboard?.resolved_themes]
  )

  // #969 Part F — Review Changes panel. Called unconditionally (before the
  // loading/error early returns below), same reason as colorMap above.
  const reviewChanges = useReviewChanges(customer, dashboard?.config)
  // Dots render only while the panel itself is open — "review mode" per the
  // issue's mode separation, not simply whenever there is something to review.
  const reviewMode = reviewPanelOpen && Boolean(dashboard?.config?.needs_review)
  const changedWidgetPaths = useMemo(
    () => new Set(reviewChanges.items.map(item => item.widgetPath)),
    [reviewChanges.items],
  )

  // Voice manifest (#1323): the viewer had none, so Douglas could open a
  // dashboard but not turn its pages or open its filters. Called before the
  // loading/error early returns below, like every hook here: below them, the
  // render that first drew a dashboard called one more hook than the spinner
  // render before it, and React threw #310 on every dashboard (prod, 11 Sep).
  const voiceConfig = dashboard?.config
  const voicePages = voiceConfig?.pages || []
  // Douglas reads this back aloud, so it carries display names rather than
  // the codes in the URL (#1347 item 1).
  const names = useDisplayNames(customer)

  useVoiceRegistration({
    pageKey: `dashboard-view:${customer}:${dashboardId}`,
    pageContext: () =>
      `Viewing the dashboard "${voiceConfig?.name || dashboardId}" for ${names.customer}. ` +
      `Pages: ${voicePages.map((p, i) => `${i + 1}. ${p.name || `Page ${i + 1}`}`).join('; ') || 'one page'}. ` +
      `On page ${activePageIndex + 1}. Quick filters restrict every widget without changing the saved dashboard.`,
    uiTools: buildVoiceTools({
      choices: [{
        name: 'go_to_page',
        label: 'the dashboard page to show',
        options: voicePages.map((p, i) => ({ value: String(i), describe: p.name || `Page ${i + 1}` })),
        optionsFrom: 'the page description (its numbered page list; the value is the number minus 1)',
        select: (v) => { setActivePageIndex(Number(v)); return `showing page ${Number(v) + 1}` },
        enabled: () => voicePages.length > 1,
      }],
      actions: [
        { name: 'open_filters', label: 'open the quick filters dialog', run: () => setFilterOpen(true) },
        { name: 'open_dashboard_settings', label: 'open the dashboard settings (about, sharing)', run: () => setAboutSettingsOpen(true) },
        { name: 'open_review_panel', label: 'open the review and sign-off panel', run: () => setReviewPanelOpen(true) },
      ],
    }),
  })

  const handleBackClick = () => {
    router.navigate('dashboards', { customer })
  }

  const handleEditClick = () => {
    router.navigate('dashboard-editor', { customer, dashboardId })
  }

  // Make your own copy, then open it (Adam, 8 Oct): the editor for a
  // dashboard, the viewer for a web app or a video, which have no editor.
  const handleDuplicateClick = async () => {
    setDuplicating(true)
    try {
      const copy = await forkDashboard(dashboardId)
      if (copy) router.navigate(dashboardTypeMeta(copy.type).opens === 'view' ? 'dashboard-view' : 'dashboard-editor', { customer, dashboardId: copy.id })
    } finally {
      setDuplicating(false)
    }
  }

  // Signing off the whole dashboard is independent of individual card state
  // (#963), but it saves the dashboard through the owner-gated PUT, so only
  // someone who can edit it in place may (canEdit below). Anyone else sees
  // Sign off disabled with the reason.
  const handleRequestSignOffDashboard = () => setSignOffConfirmOpen(true)

  // Sign-off is the publish event, so it freezes every rolling column
  // selection to what it resolves to right now (#968). Without this a new wave
  // would move a signed dashboard with nobody having edited it. Resolving
  // needs each table's column labels, which is why it happens here rather than
  // server-side, and it covers every page — a partially pinned dashboard is
  // worse than an unpinned one, so a failure aborts the sign-off.
  //
  // It also captures each table widget's column/row labels as the Review
  // Changes panel's next baseline (#969 Part F) — same reasoning: only the
  // frontend has the labels without a Carbon round trip, and a partial
  // baseline would misreport "nothing changed" for whatever wasn't captured.
  const handleConfirmSignOffDashboard = async () => {
    setSignOffConfirmOpen(false)
    if (!dashboard?.config) return

    let config = dashboard.config
    try {
      config = await pinRollingColumnsForSignOff(config, async (job, tablePath) => {
        const res = await axios.get(`${API_BASE_URL}/tables/${customer}/${job}/data`, {
          params: { path: tablePath, show_freq: true, show_colpct: true },
        })
        return extractColumnLabels(res.data)
      })
      config = await captureTableSnapshotsForSignOff(config, async (job, tablePath) => {
        const res = await axios.get(`${API_BASE_URL}/tables/${customer}/${job}/data`, {
          params: { path: tablePath, show_freq: true, show_colpct: true },
        })
        return { columns: extractColumnLabels(res.data), rows: extractRowLabels(res.data) }
      })
    } catch (err) {
      snackbar.error('Could not sign off — a chart\'s table could not be read to freeze its columns.')
      return
    }

    setReviewPanelOpen(false)
    updateDashboard(dashboard.id, { ...config, sign_off_requested: true }, dashboard.updated_at)
  }

  // "Jump to widget" from the Review Changes panel (#969 Part F): switch to
  // the item's page if needed, then scroll + briefly highlight it. Imperative
  // DOM rather than more prop-threaded state — this is a one-shot affordance,
  // not persisted UI, so it doesn't need to survive a re-render.
  const handleJumpToItem = (item: ReviewChangeItem) => {
    const jump = () => {
      const el = document.getElementById(widgetDomId(item.widgetPath))
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const previousBoxShadow = el.style.boxShadow
      el.style.transition = 'box-shadow 0.15s'
      el.style.boxShadow = '0 0 0 3px #1565c0'
      setTimeout(() => { el.style.boxShadow = previousBoxShadow }, 1500)
    }
    if (item.pageIndex !== activePageIndex) {
      setActivePageIndex(item.pageIndex)
      setTimeout(jump, 50)
    } else {
      jump()
    }
  }

  // Returns the save promise so a card's text editor can await it and keep the
  // editor open when the write is rejected (#963).
  // #1256: Gen AI card text edit / sign-off for any logged-in user — the
  // narrow endpoint, not the owner-gated whole-config PUT.
  const handleCardWrite = async (multiTableId: string, patch: CardTextWrite) => {
    if (!dashboard) return
    await writeDashboardCard(dashboard.id, multiTableId, patch, dashboard.updated_at)
  }

  const handleCellUpdate = (rowIndex: number, cellIndex: number, updatedCell: DashboardCell): Promise<void> | void => {
    if (!dashboard?.config) return
    const cfg = dashboard.config
    const pages = cfg.pages || []
    const currentPage = pages[activePageIndex]

    const updateLayout = (l: DashboardLayout): DashboardLayout => {
      const newRows = [...l.rows]
      const newCells = [...newRows[rowIndex].cells]
      newCells[cellIndex] = updatedCell
      newRows[rowIndex] = { ...newRows[rowIndex], cells: newCells }
      return { ...l, rows: newRows }
    }

    if (currentPage) {
      const newPages = [...pages]
      newPages[activePageIndex] = { ...currentPage, layout: updateLayout(currentPage.layout) }
      return updateDashboard(dashboard.id, { ...cfg, pages: newPages }, dashboard.updated_at)
    }
    return updateDashboard(dashboard.id, { ...cfg, layout: updateLayout(cfg.layout) }, dashboard.updated_at)
  }

  // Gate on auth state, not the raw query `isPending` — a disabled query (no
  // user yet) reports isPending=true forever, which otherwise spins the page
  // indefinitely on direct-URL access with an expired session.
  const loadState = resolveDashboardLoadState({
    initialized: account.initialized,
    hasUser: Boolean(account.user),
    dashboardIsLoading,
  })

  if (loadState === 'unauthenticated') {
    // Session missing/expired on direct URL access — route to login and return
    // to it afterwards (mirrors SharedDashboardView).
    account.setPostLoginRoute({ name: 'dashboard-view', params: { customer, dashboardId } })
    router.navigate('home')
  }

  if (loadState !== 'ready') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (dashboardIsError) {
    const message = dashboardError instanceof Error ? dashboardError.message : 'Failed to load dashboard'
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{message}</Alert>
      </Box>
    )
  }

  if (!dashboard) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Dashboard not found</Alert>
      </Box>
    )
  }

  // A web app bundle (a Web app or a Presentation) renders a full-height
  // iframe instead of the normal layout
  if (isWebappBundle(dashboard.type) && dashboard.config?.webapp_config) {
    const webappConfig = dashboard.config.webapp_config
    return (
      <WebappDashboardView
        customer={customer}
        dashboardId={dashboardId}
        name={dashboard.config?.name}
        config={webappConfig}
        onBack={handleBackClick}
        onEditStory={storyEditLink.canEdit ? () => void storyEditLink.openEditor(customer) : undefined}
        editOpening={storyEditLink.opening}
        editError={storyEditLink.error}
      />
    )
  }

  // A video item plays the chat's MP4. It has no editor, so Duplicate sits
  // beside Download.
  if (dashboard.type === 'video' && dashboard.config?.video_config) {
    const videoConfig = dashboard.config.video_config
    const authHeader = axios.defaults.headers.common['Authorization'] as string | undefined
    const token = authHeader?.replace('Bearer ', '')
    const base = `${API_BASE_URL}/dashboards/${customer}/${dashboardId}/video`
    const query = token ? `?token=${token}` : ''
    return (
      <VideoDashboardView
        name={dashboard.config.name || 'Video'}
        description={dashboard.config.description}
        src={`${base}${query}`}
        downloadHref={`${base}${query ? `${query}&` : '?'}download=1`}
        fileName={videoConfig.file_name}
        onBack={handleBackClick}
        topBarExtra={
          <Button
            startIcon={duplicating ? <CircularProgress size={16} /> : <ContentCopyIcon />}
            onClick={() => void handleDuplicateClick()}
            disabled={duplicating}
            variant="outlined"
            color="info"
            size="small"
          >
            Duplicate
          </Button>
        }
      />
    )
  }

  const config = dashboard.config

  // multi_table_id -> widget title, so the Sign-off history tab can name
  // cards the way the dashboard does (#974 H1).
  const widgetTitles: Record<string, string> = {}
  if (config) {
    for (const w of getMultiTableAnalysisWidgets(config)) {
      if (w.multi_table_id && w.title) widgetTitles[w.multi_table_id] = w.title
    }
  }

  // Same rule as save-in-place (#962/#964): owner, or a Manager/Realm on a
  // promoted (company-level) dashboard, may share an update (#974 H2 — the
  // copy-a-message flow that replaced the stored-subscriber notify dialog).
  const userEmail = account.user?.session_info?.email || ''
  const canManageSubscribers = canOverwriteDashboard(dashboard, userEmail, canPromoteDashboard(account.user?.role_permissions))
  // #1262: the same rule decides whether Edit is live for this viewer.
  // Anyone else sees it disabled and struck through, with a tooltip that
  // points at Duplicate on the homepage; the editor route itself bounces
  // them back here.
  const canEdit = canManageSubscribers
  // The server refuses a dashboard sign-off from anyone who cannot edit it.
  const signOffDisabledReason = canEdit ? undefined : noSignOffDashboardReason(whoToAsk, dashboard)

  const pages = config?.pages || []
  const activePage = pages[activePageIndex] || pages[0]

  const layout = activePage?.layout || config?.layout || { rows: [] }

  const tableGraphJobs = config ? getTableGraphJobs(config) : new Set<string>()
  // #1162: a dashboard's scope is its recorded job set (jobs / legacy job),
  // falling back to the jobs its widgets reference for a pre-model dashboard.
  const dashboardJobs = dashboard?.jobs?.length
    ? dashboard.jobs
    : dashboard?.job
      ? [dashboard.job]
      : [...tableGraphJobs]
  const primaryJob = dashboardJobs[0] || ''
  const isMultiJob = dashboardJobs.length > 1
  // Each job's STORED filter for this page (dashboard AND page, authored in
  // the editor), then the viewer's quick filter appended per job (#1229 C7).
  // Single-job dashboards hand the layout one resolved string as before;
  // multi-job ones hand it the per-job map.
  const storedJobFilters = config ? jobFilterMap(config, activePage, dashboardJobs, primaryJob) : {}
  const effectiveJobFilters = applyQuickFilters(storedJobFilters, quickFilters)
  const resolvedFilter = effectiveJobFilters[primaryJob] || ''
  const jobFilters = isMultiJob ? effectiveJobFilters : undefined
  const hasTableWidgets = tableGraphJobs.size > 0
  const filterDisabledReason = !hasTableWidgets ? 'Add table widgets to enable filtering' : undefined
  // Always the DISPLAY name in the UI (Matt, 3 Sep) — falling back to the
  // job name only when the session does not list the job.
  const sessionJobs = account.customer?.sessionJobs || []
  const jobLabel = (j: string) => sessionJobs.find((sj) => sj.name === j)?.displayName || j
  const activeQuickJobs = dashboardJobs.filter((j) => quickFilters[j])
  const clearQuickFilter = (j: string) => setQuickFilters((prev) => {
    const next = { ...prev }
    delete next[j]
    return next
  })
  // What every widget signposts (#1229 C6): its job's display name, stored
  // page filter and quick filter. Display only — behaviour rides the props.
  const signpostValue: FilterSignpostValue = {
    multiJob: isMultiJob,
    primaryJob,
    byJob: Object.fromEntries(dashboardJobs.map((j) => [j, {
      jobLabel: jobLabel(j),
      pageFilter: storedJobFilters[j] || '',
      quickFilter: quickFilters[j] || '',
    }])),
  }
  // The same job set the editor provides, so a picker opened from the
  // finished dashboard (a Gen AI card's Replace…) can pick any of its jobs.
  const dashboardJobsValue: DashboardJobsValue = {
    jobs: dashboardJobs.map((j) => ({ name: j, displayName: jobLabel(j) })),
    primaryJob,
  }

  return (
    // Every widget's table load names this dashboard (the dashboard grant).
    <DashboardGrantContext.Provider value={dashboardId}>
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)' }}>
      {/* Minimal header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'white',
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flexShrink: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            DataViz
          </Button>
          {/* #1183 (Matt, 2 Sep): the dashboard name sits inline after the
              back link — "← DataViz: <name>" — in the SAME font size,
              replacing the old centred h6 title. Same treatment as the
              editor, so preview and live view match. */}
          <Typography
            noWrap
            title={config?.name || 'Untitled Dashboard'}
            sx={{ fontSize: '0.8125rem', fontWeight: 500, minWidth: 0, ml: -1.75 }}
          >
            : {config?.name || 'Untitled Dashboard'}
          </Typography>
          {/* #1229 C3: the multi marker, same words as the homepage chip. */}
          {isMultiJob && (
            <Tooltip title={dashboardJobs.map(jobLabel).join(' · ')}>
              <Chip size="small" variant="outlined" label={`Multi: ${dashboardJobs.length} jobs`} sx={{ height: 20, fontSize: 11, flexShrink: 0 }} />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Quick Filter (#1229 C7, Matt 3 Sep) — the Tables term, with its
              "this is temporary" tone. Viewer-local, per job, dashboard-wide,
              appended to the stored page filter. One orange chip per job with
              an active quick filter, in the same Carbon syntax the viewer
              typed; the X clears that job only. Orange = "be aware"; the
              stored page filter beside it is blue, the edit colour. */}
          {hasTableWidgets && (
            <Tooltip title={filterDisabledReason || ''}>
              <span>
                <Button
                  startIcon={<FilterListIcon />}
                  onClick={() => { setQuickFilterJob(''); setFilterOpen(true) }}
                  size="small"
                  disabled={Boolean(filterDisabledReason)}
                  color={activeQuickJobs.length > 0 ? 'warning' : 'inherit'}
                  sx={{ textTransform: 'none' }}
                >
                  Quick Filter
                </Button>
              </span>
            </Tooltip>
          )}
          {activeQuickJobs.map((j) => {
            const label = isMultiJob ? `${jobLabel(j)}: ${quickFilters[j]}` : quickFilters[j]
            return (
              <Tooltip key={`quick-${j}`} title={`Quick filter — ${jobLabel(j)}: ${quickFilters[j]}`}>
                <Chip
                  size="small"
                  color="warning"
                  label={label}
                  data-testid="quick-filter-chip"
                  onClick={() => { setQuickFilterJob(j); setFilterOpen(true) }}
                  onDelete={() => clearQuickFilter(j)}
                  sx={{ maxWidth: 320 }}
                />
              </Tooltip>
            )
          })}
          {/* #1183: a page filter is authored in the editor; the viewer just
              needs to SEE that this page is filtered — per job on a
              multi-job dashboard. */}
          {dashboardJobs.filter((j) => storedJobFilters[j]).map((j) => (
            <Tooltip key={`page-${j}`} title={`Page filter — ${jobLabel(j)}: ${storedJobFilters[j]}`}>
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label={isMultiJob ? `Page · ${jobLabel(j)}: ${storedJobFilters[j]}` : `Page: ${storedJobFilters[j]}`}
                sx={{ maxWidth: 260 }}
              />
            </Tooltip>
          ))}
          <PageAgentButton
            pageKey="dashboard-view"
            customer={account.customer?.name || customer}
            open={helperOpen}
            onClick={() => {
              if (!helperOpen && insights.isOpen) insights.toggle()
              setHelperOpen(o => !o)
            }}
          />
          <Tooltip describeChild title={canInsights ? '' : aiAccess.refusalFor('dashboard_insights')}>
            <span>
              <Button
                startIcon={<InsightsIcon />}
                onClick={() => { if (!insights.isOpen) setHelperOpen(false); insights.toggle() }}
                disabled={!canInsights}
                size="small"
                color="inherit"
                sx={{ textTransform: 'none' }}
              >
                Insights
              </Button>
            </span>
          </Tooltip>
          <Button
            startIcon={<HistoryIcon />}
            onClick={() => setHistoryOpen(true)}
            size="small"
            color="inherit"
            sx={{ textTransform: 'none' }}
          >
            History
          </Button>
          <SignOffShareButtons
            signOff={config?.sign_off}
            signOffRevoked={config?.sign_off_revoked}
            needsReview={config?.needs_review}
            showShareUpdate={canManageSubscribers}
            signOffDisabledReason={signOffDisabledReason}
            onSignOff={handleRequestSignOffDashboard}
            onShareUpdate={() => setShareUpdateOpen(true)}
          />
          <Button
            startIcon={duplicating ? <CircularProgress size={16} /> : <ContentCopyIcon />}
            onClick={() => void handleDuplicateClick()}
            disabled={duplicating}
            variant="outlined"
            color="info"
            size="small"
          >
            Duplicate
          </Button>
          {canEdit ? (
            <Button
              startIcon={<EditIcon />}
              onClick={handleEditClick}
              variant="outlined"
              color="info"
              size="small"
            >
              Edit
            </Button>
          ) : (
            <DisabledReason reason={noEditPermissionReason(whoToAsk, dashboard)}>
              <Button startIcon={<EditOffIcon />} variant="outlined" color="info" size="small">
                Edit
              </Button>
            </DisabledReason>
          )}
        </Box>
      </Box>

      {!config?.sign_off && config?.sign_off_revoked && (
        <Box sx={{ px: 2, py: 0.75, backgroundColor: 'warning.50', borderBottom: '1px solid', borderColor: 'grey.200' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <HistoryIcon sx={{ fontSize: 16, color: 'warning.main' }} />
            <Typography variant="caption" color="warning.main">
              Dashboard sign-off{config.sign_off_revoked.previous_user ? ` by ${config.sign_off_revoked.previous_user}` : ''} was
              revoked by {config.sign_off_revoked.revoked_by}'s text edit on{' '}
              {new Date(config.sign_off_revoked.revoked_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} — sign off again when the text is right.
            </Typography>
          </Box>
        </Box>
      )}

      {config?.sign_off && (
        <Box sx={{ px: 2, py: 0.75, backgroundColor: config.needs_review ? 'warning.50' : 'success.50', borderBottom: '1px solid', borderColor: 'grey.200' }}>
          {config.needs_review ? (
            <Alert
              severity="warning"
              sx={{ py: 0 }}
              action={
                <Button color="warning" size="small" onClick={() => setReviewPanelOpen(true)} sx={{ textTransform: 'none' }}>
                  Review changes →
                </Button>
              }
            >
              {config.data_refreshed_at
                ? `Updated with new data and Gen AI context on ${new Date(config.data_refreshed_at).toLocaleDateString()} — needs re-review since the last dashboard sign-off.`
                : 'Updated with new data and Gen AI context since the last dashboard sign-off — needs re-review.'}
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="caption" color="success.main">
                Signed off by {config.sign_off.user_email} · {new Date(config.sign_off.signed_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Page tabs - only show when 2+ pages */}
      {pages.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'white', display: 'flex', justifyContent: 'center' }}>
          <Tabs
            value={activePageIndex}
            onChange={(_e, newValue) => setActivePageIndex(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none' } }}
          >
            {pages.map((page) => (
              <Tab key={page.id} label={page.name} />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Dashboard content */}
      <Box sx={{ flex: 1, p: 1.5, backgroundColor: 'grey.50' }}>
        {layout.rows.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              backgroundColor: 'white',
              border: '1px dashed',
              borderColor: 'grey.300',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              This dashboard is empty.
            </Typography>
            {canEdit ? (
              <Button
                startIcon={<EditIcon />}
                onClick={handleEditClick}
                variant="contained"
                color="info"
                sx={{ mt: 2 }}
              >
                Start Editing
              </Button>
            ) : (
              <DisabledReason reason={noEditPermissionReason(whoToAsk, dashboard)}>
                <Button startIcon={<EditOffIcon />} variant="contained" color="info" sx={{ mt: 2 }}>
                  Start Editing
                </Button>
              </DisabledReason>
            )}
          </Paper>
        ) : (
          <Paper elevation={1} sx={{ p: 1 }} key={activePage?.id || activePageIndex}>
            <DashboardJobsProvider value={dashboardJobsValue}>
            <FilterSignpostProvider value={signpostValue}>
              <DashboardLayoutComponent
                layout={layout}
                customer={customer}
                job={job}
                dashboardId={dashboardId}
                readOnly
                // Gate 2 (#1219, overwrite authority) AND gate 1 (#1537, AI
                // access level): the card's refresh and Run buttons need both.
                canRegenerate={canEdit && aiAccess.can('genai_cards')}
                globalFilter={resolvedFilter}
                jobFilters={jobFilters}
                colorMap={colorMap}
                themeColorMaps={themeColorMaps}
                dashboardConfig={config}
                currentPageIndex={activePageIndex}
                onCellUpdate={handleCellUpdate}
                onCardWrite={handleCardWrite}
                reviewMode={reviewMode}
                changedWidgetPaths={changedWidgetPaths}
              />
            </FilterSignpostProvider>
            </DashboardJobsProvider>
          </Paper>
        )}
      </Box>

      {/* Quick Filter dialog. On a multi-job dashboard the job selector sits
          inside the dialog (#1229 C7 item 6): pick the job, see and edit
          that job's quick filter; each job keeps its own. */}
      <FilterEditorModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onSave={(filter) => {
          setQuickFilters((prev) => ({ ...prev, [primaryJob]: filter }))
          setFilterOpen(false)
        }}
        currentFilter={quickFilters[primaryJob] || ''}
        customer={customer}
        job={isMultiJob ? (quickFilterJob || undefined) : primaryJob}
        jobs={isMultiJob ? dashboardJobs.map((j) => ({ name: j, displayName: jobLabel(j) })) : undefined}
        filtersByJob={quickFilters}
        onSaveForJob={(j, filter) => {
          setQuickFilters((prev) => ({ ...prev, [j]: filter }))
          setFilterOpen(false)
        }}
        hideSourceSelector
      />

      <DashboardHistoryModal
        open={historyOpen}
        customer={customer}
        dashboardId={dashboardId}
        onClose={() => setHistoryOpen(false)}
        currentSignOff={config?.sign_off}
        signOffRevoked={config?.sign_off_revoked}
        widgetTitles={widgetTitles}
      />

      {/* Review Changes panel (#969 Part F) — persists until the dashboard is
          signed off again, not until dismissed; closing it just hides it, it
          does not clear anything. */}
      <ReviewChangesPanel
        open={reviewPanelOpen}
        onClose={() => setReviewPanelOpen(false)}
        items={reviewChanges.items}
        isLoading={reviewChanges.isLoading}
        dashboardId={dashboardId}
        signOffKey={String(config?.sign_off?.signed_at ?? 'unsigned')}
        onJumpToItem={handleJumpToItem}
        onSignOff={handleRequestSignOffDashboard}
        signOffDisabledReason={signOffDisabledReason}
      />

      <PageAgentDrawer
        pageKey="dashboard-view"
        customer={account.customer?.name || customer}
        job={primaryJob}
        open={helperOpen}
        onClose={() => setHelperOpen(false)}
      />

      {/* Insights Chat Drawer (#974) */}
      <ChatDrawer
        open={insights.isOpen}
        onClose={insights.toggle}
        messages={insights.messages.slice(2)}
        onSendMessage={insights.send}
        isLoading={insights.isLoading}
        error={insights.error}
        scopeLabel={insights.boundScope?.pageName}
        framing={insightsSourceLines(insights.boundScope?.sources || [])}
        placeholder={
          insights.boundScope?.pageName
            ? `Ask about "${insights.boundScope.pageName}"...`
            : 'Ask about this dashboard...'
        }
        showAdvancedMode={false}
        personas={insightsPersonas}
        selectedPersona={insights.selectedPersona}
        onPersonaChange={insights.setSelectedPersona}
        inputHelper={
          // Context on-ramp (#974 ask 3): the helper text IS the link,
          // straight to Settings -> About.
          <Link
            component="button"
            type="button"
            variant="caption"
            onClick={() => setAboutSettingsOpen(true)}
            sx={{ textAlign: 'left' }}
          >
            Tell us what this dashboard is about so we give better insights
          </Link>
        }
      />

      {/* Settings -> About, opened from the Insights pane's context on-ramp
          (#974 ask 3). Palette/sharing props are deliberately omitted — this
          mount exists to capture Description/Objectives context. */}
      {aboutSettingsOpen && (
        <DashboardSettingsModal
          open
          initialTab="about"
          name={config?.name || ''}
          description={config?.description || ''}
          objectives={config?.objectives || ''}
          keyMetrics={config?.key_metrics || ''}
          pages={config?.pages || []}
          customer={customer}
          job={dashboard.job || job}
          dashboardId={dashboardId}
          currentSignOff={config?.sign_off}
          signOffRevoked={config?.sign_off_revoked}
          widgetTitles={widgetTitles}
          onSave={(updates) => {
            if (config) {
              updateDashboard(dashboard.id, applyDashboardSettingsSave(config, updates), dashboard.updated_at)
            }
            setAboutSettingsOpen(false)
          }}
          onClose={() => setAboutSettingsOpen(false)}
        />
      )}

      {canManageSubscribers && (
        <ShareUpdateDialog
          open={shareUpdateOpen}
          onClose={() => setShareUpdateOpen(false)}
          dashboardName={config?.name || 'Untitled Dashboard'}
          shareId={dashboard.share_id}
          shareEnabled={dashboard.share_enabled}
          signOff={config?.sign_off}
          needsReview={config?.needs_review}
          dataRefreshedAt={config?.data_refreshed_at}
          lastEditedAt={dashboard.updated_at}
          onEnableSharing={async () => {
            await shareDashboard(dashboard.id, dashboard.share_access_level || 'customer')
          }}
          enableSharingIsLoading={shareIsLoading}
        />
      )}

      {/* Confirm overwrite of manually-edited analysis text */}
      {/* Dashboard sign-off confirmation — the rule that a later edit revokes the
          sign-off is stated here, where the person signing can read it (#963). */}
      <Dialog open={signOffConfirmOpen} onClose={() => setSignOffConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sign off this dashboard</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This records that you have read this dashboard and it is ready for distribution. Your name
            and the time are logged against it.
          </DialogContentText>
          <DialogContentText>
            Any later text edit to any card on any page revokes this sign-off, and so does a data
            refresh or a regenerate. You will need to sign off again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignOffConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmSignOffDashboard} color="success" variant="contained">
            Sign off
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </DashboardGrantContext.Provider>
  )
}

export default DashboardView
